import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import CommonCard from '../../../global/card';
import CommonSkeletonLoader from '../../../global/loading';
import CommonPagination from '../../../global/pagination';
import SOURCES from '../../../../utils/constants';
import { useGenericQuery } from '../../../../queries/use-generic-query';
import { QUERY_KEYS } from '../../../../lib/query-constants';
import useDebounce from '../../../../utils/customHooks';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import EnhancedNotificationsList from './EnhancedNotificationsList';
import LocalStorageService from '../../../../utils/localStorageServices';

const EnhancedNotifications = () => {
    const { DEFAULT_RECORDS_LISTING, NOTIFICATION_UNREAD } = SOURCES;
    const queryClient = useQueryClient();

    const [searchNotification, setSearchNotification] = useState("");
    const [notificationsViewType, setNotificationsViewType] = useState(() => {
        return localStorage.getItem("notificationsViewType") || "LIST";
    });
    const [sort, setSort] = useState({ sortType: "", sortOrder: 1 });

    const [pagination, setPagination] = useState({
        offset: 0,
        limit: DEFAULT_RECORDS_LISTING,
        page: 1,
    });

    const debouncedSearchTerm = useDebounce(searchNotification, 500);

    // Mutation to mark as read
    const { mutateAsync: markAsRead } = useGenericMutation({
        endpoint: 'notification/status',
        method: "POST"
    });

    // Key params for list
    const keyParamsForList = {
        offset: pagination.offset,
        limit: pagination.limit,
        sortBy: sort.sortType,
        sortOrder: sort.sortOrder,
    };

    // Key params for search
    const searchKeyParams = {
        q: debouncedSearchTerm,
    };

    // Main data query
    const {
        data: notificationsData,
        isLoading: notificationsLoading,
        isFetching: notificationsFetching
    } = useGenericQuery({
        endpoint: "notification/list",
        queryKey: QUERY_KEYS.notifications.lists({ ...keyParamsForList }),
        queryParams: { ...keyParamsForList },
        enabled: !debouncedSearchTerm,
    });

    // Search data query
    const {
        data: searchData,
        isInitialLoading: searchDataLoading,
        isFetching: searchDataFetching
    } = useGenericQuery({
        endpoint: "notification/search",
        queryKey: QUERY_KEYS.notifications.lists({ ...searchKeyParams }),
        queryParams: { ...searchKeyParams },
        enabled: !!debouncedSearchTerm,
    });

    const currentUser = LocalStorageService.getService().getCurrentUser();
    const userTimezone = currentUser?.user?.timezone?.name;

    const customStatusNotifications = useMemo(() => {
        const custom = [];
        if (currentUser?.user && currentUser?.user?.paymentStatus === false) {
            custom.push({
                _id: "payment_pending",
                notifyText: "Please pay your outstandings.",
                link: "/profile?payment=true",
                readStatus: SOURCES.NOTIFICATION_UNREAD,
                isCustom: true,
                actionText: "Pay now",
                createdAt: new Date().toISOString()
            });
        }

        if (userTimezone === null || userTimezone === "") {
            custom.push({
                _id: "timezone_not_setup",
                notifyText: "You have not setup your timezone.",
                link: "/profile?edit=true",
                readStatus: SOURCES.NOTIFICATION_UNREAD,
                isCustom: true,
                actionText: "Setup now",
                createdAt: new Date().toISOString()
            });
        }
        return custom;
    }, [currentUser, userTimezone]);

    const onMarkAsRead = async (item) => {
        if (item.isCustom) return; // Ignore custom notifications
        if (item.readStatus === NOTIFICATION_UNREAD) {
            await markAsRead({ id: item._id });
            // Invalidate queries to refresh data
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notifications.all });
        }
    };

    // Determine final data
    const getFinalData = useMemo(() => {
        let list = [];
        if (debouncedSearchTerm) {
            list = searchData?.notifications?.notifications || searchData?.notifications || [];
        } else {
            list = notificationsData?.notifications || [];
        }

        if (!debouncedSearchTerm) {
            return [...customStatusNotifications, ...list];
        }
        return list;
    }, [notificationsData, searchData, debouncedSearchTerm, customStatusNotifications]);

    const getTotalRecords = useMemo(() => {
        let count = 0;
        if (debouncedSearchTerm) {
            count = searchData?.count || 0;
        } else {
            count = notificationsData?.count || 0;
        }
        return count + (debouncedSearchTerm ? 0 : customStatusNotifications.length);
    }, [notificationsData, searchData, debouncedSearchTerm, customStatusNotifications]);


    const isLoading = notificationsLoading || searchDataLoading;
    const isFetching = notificationsFetching || searchDataFetching;
    const dataExist = getFinalData && getFinalData.length > 0;

    return (
        <CommonCard
            isSearch={{
                show: true,
                placeholder: "Search notifications...",
                searchInput: searchNotification,
                setSearchInput: setSearchNotification
            }}
            isListGridView={{
                show: true,
                viewType: notificationsViewType,
                setViewType: setNotificationsViewType,
                viewFor: "notificationsViewType"
            }}
            isLoading={{
                show: isLoading,
                loaderCmp: (
                    <CommonSkeletonLoader
                        type={notificationsViewType === "GRID" ? "card" : "table"}
                    />
                ),
            }}
            className={(!isLoading && isFetching) ? "opacity-50" : ""}
        >
            <EnhancedNotificationsList
                isLoading={isLoading}
                notifications={getFinalData}
                sort={sort}
                setSort={setSort}
                viewType={notificationsViewType}
                onMarkAsRead={onMarkAsRead}
            />

            {(dataExist && !searchNotification) &&
                getTotalRecords > DEFAULT_RECORDS_LISTING && (
                    <CommonPagination
                        currentPage={pagination.page}
                        defaultLimit={pagination.limit}
                        totalRecords={getTotalRecords}
                        setCurrentPage={setPagination}
                        activePageRecords={getFinalData?.length || 0}
                        paginationFor="Notifications"
                        className="mt-4"
                    />
                )}
        </CommonCard>
    );
};

export default EnhancedNotifications;